import { Dropdown, Input, Select, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import CaretDownIcon from "../../../assets/icons/CaretDownIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { useGetAllReportsQuery } from "../../../features/superAdmin/ticketService/report/report.api";
import { Report } from "../../../features/superAdmin/ticketService/report/report.types";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";

export const downloadAsCSV = async (fileUrl: string) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();

    // XLSX ফাইল পড়া
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // CSV তে convert
    const csv = XLSX.utils.sheet_to_csv(worksheet);

    // Browser download trigger
    const a = document.createElement("a");
    const file = new Blob([csv], { type: "text/csv" });
    a.href = URL.createObjectURL(file);
    a.download = "report.csv";
    a.click();
  } catch (error) {
    console.log(error);
  }
};

export const downloadAsPDF = async (fileUrl: string) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();

    // XLSX ফাইল পড়া
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);

    // PDF বানানো
    const doc = new jsPDF();
    doc.text("Report Data", 10, 10);

    jsonData.forEach((row, i) => {
      doc.text(JSON.stringify(row), 10, 20 + i * 10);
    });

    doc.save("report.pdf");
  } catch (error) {
    console.log(error);
  }
};

const STATUS_CONFIG = {
  success: "status status-success",
  pending: "status status-yellow",
  failed: "status status-danger",
};

const Reports = () => {
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useTable({
    defaultFilter: { status: "all" },
  });
  const { data, isLoading, isError, error } = useGetAllReportsQuery({
    params: {
      page,
      limit,
      search: keyword,
      status: filter.status === "all" ? undefined : filter.status,
    },
  });

  // ------ Errors ------
  useError(isError, error, "Reports get failed.");

  const columns: ColumnsType<Report> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      key: "name",
      dataIndex: "name",
      title: "Generated for",
      render: (val) => (val ? val : "N/A"),
    },
    {
      key: "date-range",
      title: "Report Date Range",
      render: ({ startDate, endDate }) => (
        <Space>
          {startDate && endDate
            ? `${formatDate(startDate)} to ${formatDate(endDate)}`
            : ""}
        </Space>
      ),
    },
    {
      key: "date-range",
      title: "Created at",
      dataIndex: "created_at",
      render: (val) => formatDate(val),
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      align: "center" as const,
      render: (status) => (
        <div
          className={
            STATUS_CONFIG?.[
              (status as string)?.toLowerCase() as keyof typeof STATUS_CONFIG
            ]
          }
        >
          {capitalize(status)}
        </div>
      ),
    },
    {
      key: "action",
      title: "Action",
      align: "center" as const,
      render: ({ fileUrl, status }) => {
        return (
          <div className="flex items-center justify-center">
            <Dropdown
              disabled={!(status === "success")}
              overlayClassName="w-36"
              placement="bottomRight"
              menu={{
                items: [
                  ...(status === "success"
                    ? [
                        // {
                        //   key: "0",
                        //   label: "CSV",
                        //   onClick: () => downloadAsCSV(fileUrl),
                        // },
                        {
                          key: "1",
                          label: "XLSX",
                          onClick: () => {
                            const url = fileUrl;
                            if (!url) return;
                            const a = document.createElement("a");
                            a.href = url;
                            a.download =
                              url.split("/").pop() || "ticket-reports.xlsx";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          },
                        },
                        // {
                        //   key: "2",
                        //   label: "PDF",
                        //   onClick: () => downloadAsPDF(fileUrl),
                        // },
                      ]
                    : []),
                ],
              }}
              trigger={["click"]}
            >
              <Space className="cursor-pointer">
                Download <CaretDownIcon />
              </Space>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <SidebarLayout title="Report">
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2 items-center">
          <div>Total Rows</div>
          <Select
            placeholder="10"
            suffixIcon={<CaretDownIcon />}
            size="middle"
            style={{ width: 70 }}
            onChange={handleLimit}
            options={[
              { label: "10", value: 10 },
              { label: "20", value: 20 },
              { label: "50", value: 50 },
              { label: "100", value: 100 },
              { label: "1000", value: 1000 },
            ]}
          />
          <div>of {data?.data?.totalLength}</div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search by generated for"
            suffix={<SearchIcon />}
            size="middle"
            className="max-w-[200px]"
            onChange={handleSearch}
          />
          <Select
            allowClear
            placeholder="Status"
            suffixIcon={<CaretDownIcon />}
            size="middle"
            style={{ width: 120 }}
            value={filter.status}
            onChange={(value) => handleFilter("status", value)}
            options={[
              { label: "All", value: "all" },
              { label: "Pending", value: "pending" },
              { label: "Success", value: "success" },
              { label: "Failed", value: "failed" },
            ]}
          />
        </div>
      </div>
      <CustomTable
        page={page}
        limit={limit}
        columns={columns}
        loading={isLoading}
        filterSection={<></>}
        topSectionClassName="w-full"
        className="designationTable"
        dataSource={data?.data?.reports}
        total={data?.data?.totalLength || 0}
        handlePagination={handlePagination}
      />
    </SidebarLayout>
  );
};

export default Reports;
